import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { 
  Sparkles, 
  Users, 
  Briefcase, 
  TrendingUp, 
  FileText, 
  BarChart,
  Building2,
  Globe,
  PhoneCall,
  Download,
  Upload,
  Loader2,
  ArrowLeftRight
} from 'lucide-react';
import BackButton from '../../../components/admin/BackButton';

const API = process.env.REACT_APP_BACKEND_URL;

const HomepageManagement = () => {
  const fileInputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

  const handleExport = async () => {
    try {
      setBusy(true); setStatus(null);
      const { data } = await axios.get(`${API}/api/homepage-ml/admin/export-all`, { headers: authHeaders() });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `homepage-content-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setStatus({ type: 'success', msg: `Exportadas ${data.count} secções (todos os idiomas). Ficheiro descarregado.` });
    } catch (e) {
      setStatus({ type: 'error', msg: `Falha ao exportar: ${e?.response?.data?.detail || e.message}` });
    } finally { setBusy(false); }
  };

  const handleImportFile = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = '';
    if (!file) return;
    if (!window.confirm('Importar irá SUBSTITUIR o conteúdo atual da homepage (todas as secções e idiomas). Continuar?')) return;
    try {
      setBusy(true); setStatus(null);
      const payload = JSON.parse(await file.text());
      const { data } = await axios.post(`${API}/api/homepage-ml/admin/import-bulk`, payload, {
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      });
      setStatus({ type: 'success', msg: `Importação concluída: ${data.updated} atualizadas, ${data.created} criadas, ${data.failed} falhas.` });
    } catch (e) {
      setStatus({ type: 'error', msg: `Falha ao importar: ${e?.response?.data?.detail || e.message}` });
    } finally { setBusy(false); }
  };

  const sections = [
    {
      id: 'hero',
      title: 'Hero Section',
      description: 'Primeira secção da homepage - título, subtítulo e botões',
      icon: Sparkles,
      path: '/admin/homepage/hero',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      id: 'about',
      title: 'About Section',
      description: 'Informações sobre o Grupo BMP, história e estatísticas',
      icon: Building2,
      path: '/admin/homepage/about',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      id: 'services',
      title: 'Services Section',
      description: 'Serviços oferecidos (Consultoria, Digital, Operações, etc.)',
      icon: Briefcase,
      path: '/admin/homepage/services',
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      id: 'highlights',
      title: 'Highlights (Trading/Procurement)',
      description: 'Secções destacadas de Trading Global e Procurement',
      icon: TrendingUp,
      path: '/admin/homepage/highlights/trading',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    },
    {
      id: 'projects',
      title: 'Projects Section',
      description: 'Projetos em destaque e casos de sucesso',
      icon: FileText,
      path: '/admin/homepage/projects',
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50'
    },
    {
      id: 'stats',
      title: 'Stats Section',
      description: 'Estatísticas (anos, projetos, continentes, etc.)',
      icon: BarChart,
      path: '/admin/homepage/stats',
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-50'
    },
    {
      id: 'clients',
      title: 'Clients Section',
      description: 'Logos e testemunhos de clientes',
      icon: Users,
      path: '/admin/homepage/clients',
      color: 'text-pink-600',
      bgColor: 'bg-pink-50'
    },
    {
      id: 'insights',
      title: 'Insights Section',
      description: 'Artigos, blog posts e atualizações',
      icon: Globe,
      path: '/admin/homepage/insights',
      color: 'text-teal-600',
      bgColor: 'bg-teal-50'
    },
    {
      id: 'cta',
      title: 'CTA Section',
      description: 'Call-to-action final da homepage',
      icon: PhoneCall,
      path: '/admin/homepage/cta',
      color: 'text-red-600',
      bgColor: 'bg-red-50'
    },
    {
      id: 'footer',
      title: 'Footer & Contact Info',
      description: 'Rodapé, email, telefone, escritórios e redes sociais',
      icon: PhoneCall,
      path: '/admin/homepage/footer',
      color: 'text-slate-600',
      bgColor: 'bg-slate-50'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-6 max-w-7xl">
        <BackButton />

        <div className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-3xl">Gestão da Homepage</CardTitle>
              <CardDescription className="text-base">
                Edite o conteúdo de cada secção da homepage. Todas as secções são multilíngues (PT/EN).
              </CardDescription>
            </CardHeader>

            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sections.map((section) => {
                  const Icon = section.icon;
                  
                  return (
                    <Link key={section.id} to={section.path}>
                      <Card className="hover:shadow-lg transition-all duration-300 hover:scale-105 cursor-pointer h-full">
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <div className={`p-3 rounded-lg ${section.bgColor}`}>
                              <Icon className={`w-6 h-6 ${section.color}`} />
                            </div>
                            
                            <div className="flex-1">
                              <h3 className="font-semibold text-lg mb-2 text-[#2B2D42]">
                                {section.title}
                              </h3>
                              <p className="text-sm text-gray-600 mb-4">
                                {section.description}
                              </p>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-[#00B4D8] hover:text-[#48CAE4] p-0"
                              >
                                Editar →
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Content Migration (Preview -> Production) */}
          <Card className="mt-6 border-2 border-[#00B4D8]/30" data-testid="homepage-migration-card">
            <CardHeader>
              <div className="flex items-center gap-2">
                <ArrowLeftRight className="w-5 h-5 text-[#00B4D8]" />
                <CardTitle>Migração de Conteúdo (Preview → Produção)</CardTitle>
              </div>
              <CardDescription className="text-base">
                Exporte o conteúdo totalmente traduzido (PT/EN/FR/AR/ZH, incluindo cards, pilares,
                artigos e estatísticas) deste ambiente e importe-o noutro. Use-o para alinhar a
                produção com o preview sem depender da tradução automática.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={handleExport}
                  disabled={busy}
                  className="bg-[#00B4D8] hover:bg-[#48CAE4] text-white"
                  data-testid="homepage-export-btn"
                >
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Exportar conteúdo
                </Button>
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={busy}
                  variant="outline"
                  className="border-[#00B4D8] text-[#00B4D8] hover:bg-[#00B4D8]/10"
                  data-testid="homepage-import-btn"
                >
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  Importar conteúdo
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/json,.json"
                  className="hidden"
                  onChange={handleImportFile}
                  data-testid="homepage-import-input"
                />
              </div>
              {status && (
                <div
                  className={`mt-4 text-sm px-4 py-3 rounded-lg ${
                    status.type === 'success'
                      ? 'bg-green-50 text-green-800 border border-green-200'
                      : 'bg-red-50 text-red-800 border border-red-200'
                  }`}
                  data-testid="homepage-migration-status"
                >
                  {status.msg}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Informação</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Globe className="w-5 h-5 text-blue-600" />
                    <h4 className="font-semibold text-blue-900">Multilíngue</h4>
                  </div>
                  <p className="text-sm text-blue-700">
                    Todas as secções suportam Português e Inglês com tradução automática GPT-5.2
                  </p>
                </div>

                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-5 h-5 text-green-600" />
                    <h4 className="font-semibold text-green-900">Upload de Imagens</h4>
                  </div>
                  <p className="text-sm text-green-700">
                    Bunny.net CDN, biblioteca de imagens e sugestões AI disponíveis
                  </p>
                </div>

                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                    <h4 className="font-semibold text-purple-900">Atualização Instantânea</h4>
                  </div>
                  <p className="text-sm text-purple-700">
                    Alterações aparecem imediatamente na homepage pública
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default HomepageManagement;
